import { IMGS_PATH, LINK_SHARE_IMAGE } from 'constants/image';
import { format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import Head from 'next/head';
import { getBreadcrumbStructuredData } from 'components/ui/Breadcrumbs';
import config from 'config';
import { getAuthorsBaseSlug } from 'data/utils/author';
import { useTranslation } from 'hooks/useTranslation';

import type {
  GuidePage,
  TreatmentPage,
} from 'types/contentful/generated/graphql';

type Props = {
  crumbs: { link: string; title: string }[];
  entry: TreatmentPage | GuidePage;
  isGuidePage?: boolean;
};

const GuideOrTreatmentStructuredData = ({
  crumbs,
  entry,
  isGuidePage,
}: Props): JSX.Element => {
  const { locale, t } = useTranslation();
  const authorsBaseSlug = getAuthorsBaseSlug(locale);
  const { medicallyReviewed, pageAuthor, title, slug, metaDescription, sys } =
    entry;
  const { production } = config.app.domains.proxy;
  const baseUrl = `https://${production}/${locale}`;

  const editorialPolicy = ((locale) => {
    switch (locale) {
      case 'en':
        return 'editorial-policy';
      case 'de':
        return 'redaktionelle-richtlinien';
      case 'nl':
        return 'redactioneel-beleid';
      case 'fr':
        return 'politique-editoriale';
      case 'es':
        return 'politica-editorial';
      case 'it':
        return 'politica-editoriale';
    }
  })(locale);

  const authorSchema = {
    author: {
      '@type': 'Person',
      name: pageAuthor?.name,
      description: pageAuthor?.metaDescription,
      jobTitle: pageAuthor?.jobTitle,
      knowsAbout: pageAuthor?.knowsAbout,
    },
  };

  const reviewedBySchema = {
    reviewedBy: {
      '@type': 'Person',
      name: medicallyReviewed?.name,
      description: medicallyReviewed?.metaDescription,
      jobTitle: medicallyReviewed?.jobTitle,
      honorificPrefix: medicallyReviewed?.honorificPrefix,
      honorificSuffix: medicallyReviewed?.honorificSuffix,
      sameAs:
        medicallyReviewed?.sameAs ||
        `${baseUrl}/${authorsBaseSlug}/${medicallyReviewed?.slug}`,
      knowsAbout: medicallyReviewed?.knowsAbout,
      alumniOf: {
        '@type': 'EducationalOrganization',
        name: medicallyReviewed?.alumniOfName,
      },
    },
  };

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    datePublished: sys.firstPublishedAt,
    dateModified: sys.publishedAt,
    ...(pageAuthor && authorSchema),
    description: metaDescription,
    audience: {
      '@type': 'MedicalAudience',
      audienceType: 'patient',
    },
    mainEntityOfPage: {
      '@type': 'MedicalWebPage',
      '@id': `${baseUrl}${isGuidePage ? '/guides' : ''}/${slug}`,
      breadcrumb: getBreadcrumbStructuredData(crumbs, true),
      ...(medicallyReviewed && reviewedBySchema),
      lastReviewed: format(parseISO(sys.publishedAt), 'yyyy-MM-dd', {
        locale: enUS,
      }),
    },
    image: LINK_SHARE_IMAGE,
    publisher: {
      '@type': 'Organization',
      name: t('qunomedical'),
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `https://${production}${IMGS_PATH.PUBLIC}/qunomedical-logo.svg`,
        width: '494',
        height: '71',
      },
      brand: { '@type': 'Thing', name: t('qunomedical') },
      publishingPrinciples: `${baseUrl}/${authorsBaseSlug}/${editorialPolicy}`,
      sameAs: [
        'https://www.linkedin.com/company/qunomedical',
        'https://www.instagram.com/qunomedical/',
        'https://twitter.com/qunomedical',
        'https://www.facebook.com/qunomedical/',
      ],
    },
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(baseSchema) }}
      />
    </Head>
  );
};

export default GuideOrTreatmentStructuredData;
